import Apple from "@/assets/images/socials/apple.svg";
import Facebook from "@/assets/images/socials/facebook.svg";
import Google from "@/assets/images/socials/google.svg";
import { Button, FormControl, Input, Modal } from "@/components";
import { authSdk } from "@/modules/classes/auth-sdk";
import {
  api,
  APPLE_SERVICE_ID,
  FACEBOOK_APP_ID,
  firstNameValidation,
  GOOGLE_CLIENT_ID,
  lastNameValidation,
} from "@/utils";
import polyglot from "@/utils/polyglot";
import { Field, Form, Formik } from "formik";
import { useEffect, useState } from "react";
import { useModals } from "react-easy-modals";
import * as yup from "yup";

const GOOGLE_PROVIDER = "google";
const APPLE_PROVIDER = "apple";
const FACEBOOK_PROVIDER = "facebook";

// Validation schema for completing social login information
const completeInformationValidationSchema = yup.object().shape({
  email: yup
    .string()
    .email(polyglot.t("common.errors.email_invalid"))
    .required(polyglot.t("common.errors.field_required")),
  ...lastNameValidation,
  ...firstNameValidation,
});

export const CompleteInformationModal = ({
  close,
  isOpen,
  email,
  first_name,
  last_name,
}) => {
  const handleSubmit = (values) => {
    close(values);
  };

  return (
    <Modal.XSmall
      isOpen={isOpen}
      onClose={close}
      shouldCloseOnOverlayClick={false}
    >
      <Modal.Item.Header isOpen={isOpen} onClose={close}>
        <Modal.Item.Title>
          {polyglot.t("signup.finish_signup")}
        </Modal.Item.Title>
      </Modal.Item.Header>
      <Formik
        initialValues={{
          first_name: first_name || "",
          last_name: last_name || "",
          email: email || "",
        }}
        validateOnMount
        validationSchema={completeInformationValidationSchema}
        onSubmit={handleSubmit}
      >
        {({ errors, touched, isValid }) => (
          <Form>
            <Modal.Item.Wrapper>
              <Field name="email">
                {({ field }) => (
                  <FormControl
                    label={polyglot.t("common.email")}
                    error={touched.email && errors.email}
                  >
                    <Input
                      {...field}
                      type="text"
                      placeholder={polyglot.t("common.placeholder.email")}
                    />
                  </FormControl>
                )}
              </Field>

              <Field name="first_name">
                {({ field }) => (
                  <FormControl
                    label={polyglot.t("common.first_name")}
                    error={touched.first_name && errors.first_name}
                  >
                    <Input
                      {...field}
                      type="text"
                      placeholder={polyglot.t("common.placeholder.first_name")}
                    />
                  </FormControl>
                )}
              </Field>

              <Field name="last_name">
                {({ field }) => (
                  <FormControl
                    label={polyglot.t("common.last_name")}
                    error={touched.last_name && errors.last_name}
                  >
                    <Input
                      {...field}
                      type="text"
                      placeholder={polyglot.t("common.placeholder.last_name")}
                    />
                  </FormControl>
                )}
              </Field>
            </Modal.Item.Wrapper>
            <Modal.Item.Footer column>
              <Button.Large type="submit" block disabled={!isValid}>
                {polyglot.t("common.signup")}
              </Button.Large>
            </Modal.Item.Footer>
          </Form>
        )}
      </Formik>
    </Modal.XSmall>
  );
};

export const useAuth = () => {
  const redirectUri = `${window.location.origin}/oauth/callback`;
  const modals = useModals();
  const [providers] = useState([
    { id: GOOGLE_PROVIDER, name: "Google", icon: Google },
    { id: APPLE_PROVIDER, name: "Apple", icon: Apple },
    { id: FACEBOOK_PROVIDER, name: "Facebook", icon: Facebook },
  ]);

  const sync = (values = {}) => api.post("/sessions/sync", values);

  const verifyEmail = (email) =>
    api.get("/sessions/new", { params: { email } });

  const formatRedirectionUrl = (url, data = {}) => {
    const { url_params, user_country_host } = data;
    let separator = url.includes("?") ? "&" : "?";
    const query = url_params ? new URLSearchParams(url_params).toString() : "";
    separator = !query ? "" : separator;
    if (user_country_host) {
      return `https://${user_country_host}${url}${separator}${query}`;
    }
    return `${url}${separator}${query}`;
  };

  useEffect(() => {
    authSdk.initializeAll();
  }, []);

  const signUp = async (
    values = {} // values
  ) =>
    // user: { email:string, password:string, company_name:string, is_pro:boolean },
    // remember_me: false,
    // zipcode: string,
    // place_id: string,
    // city_id: string,
    // alert_setting: {
    //   coreg: boolean,
    //   optin: boolean,
    // },
    api.post("/users", values);
  const signIn = async (provider, values = {}) =>
    // values
    // user: { email: "", password: "" },

    // ...rest,
    new Promise((resolve, reject) => {
      switch (provider) {
        case GOOGLE_PROVIDER: {
          const scope = "openid email profile";
          const responseType = "token id_token";
          const nonce = Math.random().toString(36).substring(2, 15);

          const authUrl =
            `https://accounts.google.com/o/oauth2/v2/auth?` +
            `client_id=${GOOGLE_CLIENT_ID}&` +
            `redirect_uri=${encodeURIComponent(redirectUri)}&` +
            `response_type=${encodeURIComponent(responseType)}&` +
            `scope=${encodeURIComponent(scope)}&` +
            `nonce=${nonce}&` +
            `prompt=select_account`;

          // Center the popup modal on the screen
          const popupWidth = 500;
          const popupHeight = 600;
          const left = window.screenX + (window.outerWidth - popupWidth) / 2;
          const top = window.screenY + (window.outerHeight - popupHeight) / 2;
          const popup = window.open(
            authUrl,
            "google-oauth",
            `width=${popupWidth},height=${popupHeight},scrollbars=yes,resizable=yes,top=${top},left=${left},status=no,menubar=no,toolbar=no,location=no`
          );

          // Check if popup was blocked
          if (!popup) {
            reject(new Error("Popup was blocked by the browser"));
            break;
          }

          // Declare checkClosed variable
          let checkClosed;

          // Listen for postMessage from popup
          const handleMessage = (event) => {
            // Verify origin for security
            if (event.origin !== window.location.origin) {
              return;
            }

            if (
              event.data.type === "GOOGLE_AUTH_SUCCESS" &&
              event.data.idToken
            ) {
              clearInterval(checkClosed);
              window.removeEventListener("message", handleMessage);
              popup.close();
              const oauthToken = event.data.idToken;
              sync({
                provider: "google",
                oauth_token: oauthToken,
                address_country: polyglot.country,
              })
                .then(async (response) => {
                  if (response.data.action === "create_user") {
                    await signUp({
                      ...values,
                      user: {
                        email: response.data.email,
                        first_name: response.data.first_name,
                        last_name: response.data.last_name,
                        password: null,
                        is_pro: false,
                        kind: "individual",
                      },
                      provider: "google",
                      oauth_token: oauthToken,
                      alert_setting: {
                        coreg: true,
                        optin: true,
                      },
                    })
                      .then(() => resolve(response))
                      .catch((error) => {
                        reject(new Error(`Login error: ${error}`));
                      });
                  } else {
                    resolve(response);
                  }
                })
                .catch((error) => {
                  reject(error);
                });
            } else if (event.data.type === "GOOGLE_AUTH_ERROR") {
              clearInterval(checkClosed);
              window.removeEventListener("message", handleMessage);
              popup.close();
              reject(
                new Error(event.data.error || "Google authentication failed")
              );
            }
          };

          // Monitor popup closure
          checkClosed = setInterval(() => {
            if (popup.closed) {
              clearInterval(checkClosed);
              window.removeEventListener("message", handleMessage);
              reject(new Error("Authentication was cancelled by user"));
            }
          }, 500);

          // Add event listener for postMessage
          window.addEventListener("message", handleMessage);

          break;
        }
        case APPLE_PROVIDER: {
          window.AppleID.auth.init({
            clientId: APPLE_SERVICE_ID,
            scope: "name email",
            redirectURI: redirectUri,
            state: "apple-signin",
            usePopup: true,
          });
          window.AppleID.auth
            .signIn()
            .then(async (appleResponse) => {
              let firstName = appleResponse.user?.name?.firstName;
              let lastName = appleResponse.user?.name?.lastName;
              let email = appleResponse.user?.email;
              const oauthToken = appleResponse.authorization.id_token;

              sync({
                provider: "apple",
                oauth_token: oauthToken,
                address_country: polyglot.country,
                first_name: firstName,
                last_name: lastName,
              })
                .then(async (response) => {
                  const isSignup = response.data.action === "create_user";
                  const isComplete =
                    !!response.data.first_name &&
                    !!response.data.last_name &&
                    !!response.data.email;

                  if (isSignup) {
                    if (!isComplete) {
                      const userInput = await modals.open(
                        CompleteInformationModal,
                        {
                          email,
                          first_name: firstName,
                          last_name: lastName,
                        }
                      );
                      firstName = userInput.first_name;
                      lastName = userInput.last_name;
                      email = userInput.email;
                    } else {
                      firstName = response.data.first_name;
                      lastName = response.data.last_name;
                      email = response.data.email;
                    }
                    await signUp({
                      ...values,
                      user: {
                        email,
                        first_name: firstName,
                        last_name: lastName,
                        password: null,
                        is_pro: false,
                        kind: "individual",
                      },
                      provider: "apple",
                      oauth_token: oauthToken,
                      alert_setting: {
                        coreg: true,
                        optin: true,
                      },
                    })
                      .then(() => resolve(response))
                      .catch((error) => {
                        reject(new Error(`Login error: ${error}`));
                      });
                  } else {
                    resolve(response);
                  }
                  resolve(response);
                })
                .catch((error) => {
                  reject(error);
                });
            })
            .catch((error) => {
              reject(error);
            });
          break;
        }

        case FACEBOOK_PROVIDER:
          window.FB.init({
            appId: FACEBOOK_APP_ID,
            cookie: true,
            xfbml: true,
            version: "v22.0",
          });
          window.FB.login(
            (responseFacebook) => {
              if (responseFacebook.authResponse) {
                const oauthToken = responseFacebook.authResponse.accessToken;
                sync({
                  provider: "facebook",
                  oauth_token: oauthToken,
                  address_country: polyglot.country,
                })
                  .then(async (response) => {
                    const isSignup = response.data.action === "create_user";
                    const isComplete =
                      !!response.data.first_name &&
                      !!response.data.last_name &&
                      !!response.data.email;

                    if (isSignup) {
                      let firstName = response.data?.first_name;
                      let lastName = response.data?.last_name;
                      let email = response.data?.email;

                      if (!isComplete) {
                        const userInput = await modals.open(
                          CompleteInformationModal,
                          {
                            email,
                            first_name: firstName,
                            last_name: lastName,
                          }
                        );
                        firstName = userInput.first_name;
                        lastName = userInput.last_name;
                        email = userInput.email;
                      }

                      await signUp({
                        ...values,
                        user: {
                          email,
                          first_name: firstName,
                          last_name: lastName,
                          password: null,
                          is_pro: false,
                          kind: "individual",
                        },
                        provider: "facebook",
                        oauth_token: oauthToken,
                        alert_setting: {
                          coreg: true,
                          optin: true,
                        },
                      })
                        .then(() => resolve(response))
                        .catch((error) => {
                          reject(new Error(`Login error: ${error}`));
                        });
                    } else {
                      resolve(response);
                    }
                  })
                  .catch((error) => {
                    reject(error);
                  });
              } else {
                reject(error);
              }
            },
            { scope: "email,public_profile" }
          );
          break;

        default:
          api
            .post("/sessions", values)
            .then((response) => {
              resolve(response);
            })
            .catch((error) => {
              reject(error);
            });
      }
    });

  const redirect = (path, data) => {
    const forceRedirection = document.getElementById("force-redirection");
    if (forceRedirection) {
      window.location = formatRedirectionUrl(
        forceRedirection.getAttribute("href"),
        data
      );
    }
    window.location = formatRedirectionUrl(path, data);
  };

  return { signIn, signUp, providers, verifyEmail, redirect };
};
